import { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Avatar,
  IconButton,
  Divider,
  Chip
} from '@mui/material';
import { PersonAdd, Save, PhotoCamera, Delete, DocumentScanner, CheckCircle } from '@mui/icons-material';
import { patientAPI } from '../services/api';
import CameraCapture from './CameraCapture';

function PatientRegistration({ onRegistrationComplete }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: 'male',
    phone: '',
    email: '',
    address: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    photoBlob: '',
    photoMimeType: '',
    aadharNumber: '',
    fatherHusbandName: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);

  // Aadhar scan state
  const [aadharFrontPreview, setAadharFrontPreview] = useState(null);
  const [aadharBackPreview, setAadharBackPreview] = useState(null);
  const [scanningFront, setScanningFront] = useState(false);
  const [scanningBack, setScanningBack] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const frontInputRef = useRef();
  const backInputRef = useRef();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCameraCapture = (imageData, mimeType) => {
    setFormData({ ...formData, photoBlob: imageData, photoMimeType: mimeType });
    setPhotoPreview(imageData);
    setCameraOpen(false);
  };

  const handleRemovePhoto = () => {
    setFormData({ ...formData, photoBlob: '', photoMimeType: '' });
    setPhotoPreview(null);
  };

  const readFileAsBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleAadharScan = async (file, side) => {
    if (!file) return;
    const setScanningState = side === 'front' ? setScanningFront : setScanningBack;
    const setPreview = side === 'front' ? setAadharFrontPreview : setAadharBackPreview;

    try {
      setScanningState(true);
      const base64 = await readFileAsBase64(file);
      setPreview(base64);

      const mimeType = file.type || 'image/jpeg';
      const res = await patientAPI.scanAadhar(base64, mimeType);
      const data = res.data.data;

      setScanResult(data);

      // Prefill form fields from scanned data
      setFormData(prev => ({
        ...prev,
        firstName: data.name ? data.name.split(' ')[0] : prev.firstName,
        lastName: data.name && data.name.split(' ').length > 1
          ? data.name.split(' ').slice(1).join(' ')
          : prev.lastName,
        dateOfBirth: data.dateOfBirth || prev.dateOfBirth,
        gender: data.gender || prev.gender,
        address: data.address || prev.address,
        aadharNumber: data.aadharNumber || prev.aadharNumber,
        fatherHusbandName: data.fatherHusbandName || prev.fatherHusbandName
      }));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to scan Aadhar card. Please fill details manually.');
    } finally {
      setScanningState(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.aadharNumber && formData.aadharNumber.length !== 12) {
      setError('Aadhar number must be exactly 12 digits');
      return;
    }

    setLoading(true);
    try {
      const response = await patientAPI.register({
        ...formData,
        aadharFrontImage: aadharFrontPreview || null,
        aadharBackImage: aadharBackPreview || null
      });
      const data = response.data.data || response.data;
      setSuccess(`Patient registered successfully! UHID: ${data.patientCode}`);

      setFormData({
        firstName: '', lastName: '', dateOfBirth: '', gender: 'male',
        phone: '', email: '', address: '', emergencyContactName: '',
        emergencyContactPhone: '', photoBlob: '', photoMimeType: '',
        aadharNumber: '', fatherHusbandName: ''
      });
      setPhotoPreview(null);
      setAadharFrontPreview(null);
      setAadharBackPreview(null);
      setScanResult(null);

      if (onRegistrationComplete) onRegistrationComplete(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card elevation={2}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <PersonAdd sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Typography variant="h5" component="h2" fontWeight="bold">
            Patient Registration
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>

        {/* Aadhar Scan Section */}
        <Box sx={{ mb: 3, p: 2, border: 1, borderColor: 'primary.light', borderRadius: 1, bgcolor: 'primary.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <DocumentScanner color="primary" />
            <Typography variant="subtitle1" fontWeight="bold" color="primary">
              Scan Aadhar Card to Auto-fill
            </Typography>
            {scanResult && <Chip icon={<CheckCircle />} label="Data extracted" color="success" size="small" />}
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Upload Aadhar front/back images to automatically fill patient details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <input
                ref={frontInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={e => { handleAadharScan(e.target.files?.[0], 'front'); e.target.value = ''; }}
              />
              <Box
                sx={{
                  border: '2px dashed', borderColor: aadharFrontPreview ? 'success.main' : 'primary.main',
                  borderRadius: 1, p: 1.5, textAlign: 'center', cursor: 'pointer',
                  bgcolor: aadharFrontPreview ? 'success.50' : 'background.paper',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => frontInputRef.current?.click()}
              >
                {aadharFrontPreview ? (
                  <Box>
                    <img src={aadharFrontPreview} alt="Aadhar Front" style={{ maxHeight: 80, maxWidth: '100%', borderRadius: 4 }} />
                    {scanningFront && <CircularProgress size={16} sx={{ ml: 1 }} />}
                  </Box>
                ) : (
                  <Box>
                    {scanningFront
                      ? <CircularProgress size={24} />
                      : <DocumentScanner sx={{ fontSize: 32, color: 'primary.main' }} />
                    }
                    <Typography variant="caption" display="block">
                      {scanningFront ? 'Scanning...' : 'Upload Aadhar Front'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <input
                ref={backInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={e => { handleAadharScan(e.target.files?.[0], 'back'); e.target.value = ''; }}
              />
              <Box
                sx={{
                  border: '2px dashed', borderColor: aadharBackPreview ? 'success.main' : 'primary.main',
                  borderRadius: 1, p: 1.5, textAlign: 'center', cursor: 'pointer',
                  bgcolor: aadharBackPreview ? 'success.50' : 'background.paper',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => backInputRef.current?.click()}
              >
                {aadharBackPreview ? (
                  <Box>
                    <img src={aadharBackPreview} alt="Aadhar Back" style={{ maxHeight: 80, maxWidth: '100%', borderRadius: 4 }} />
                    {scanningBack && <CircularProgress size={16} sx={{ ml: 1 }} />}
                  </Box>
                ) : (
                  <Box>
                    {scanningBack
                      ? <CircularProgress size={24} />
                      : <DocumentScanner sx={{ fontSize: 32, color: 'primary.main' }} />
                    }
                    <Typography variant="caption" display="block">
                      {scanningBack ? 'Scanning...' : 'Upload Aadhar Back'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ mb: 3 }}>
          <Typography variant="caption" color="text.secondary">OR FILL MANUALLY</Typography>
        </Divider>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name *"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name *"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Date of Birth *"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Gender *"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone *"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Aadhar Number"
                name="aadharNumber"
                value={formData.aadharNumber}
                onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 12);
                  setFormData(f => ({ ...f, aadharNumber: val }));
                }}
                inputProps={{ maxLength: 12 }}
                helperText="12-digit Aadhar number (optional, must be unique if provided)"
                error={formData.aadharNumber.length > 0 && formData.aadharNumber.length !== 12}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Father / Husband Name *"
                name="fatherHusbandName"
                value={formData.fatherHusbandName}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="email"
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Emergency Contact Name"
                name="emergencyContactName"
                value={formData.emergencyContactName}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Emergency Contact Phone"
                name="emergencyContactPhone"
                value={formData.emergencyContactPhone}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Patient Photo
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                  {photoPreview ? (
                    <Box sx={{ position: 'relative' }}>
                      <Avatar src={photoPreview} sx={{ width: 120, height: 120 }} variant="rounded" />
                      <IconButton
                        onClick={handleRemovePhoto}
                        size="small"
                        sx={{
                          position: 'absolute', top: -8, right: -8,
                          bgcolor: 'error.main', color: 'white',
                          '&:hover': { bgcolor: 'error.dark' }
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Avatar sx={{ width: 120, height: 120, bgcolor: 'grey.200' }} variant="rounded">
                      <PersonAdd sx={{ fontSize: 60, color: 'grey.400' }} />
                    </Avatar>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<PhotoCamera />}
                    onClick={() => setCameraOpen(true)}
                  >
                    {photoPreview ? 'Retake Photo' : 'Take Photo'}
                  </Button>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
                fullWidth
              >
                {loading ? 'Registering...' : 'Register Patient'}
              </Button>
            </Grid>
          </Grid>
        </form>

        <CameraCapture
          open={cameraOpen}
          onClose={() => setCameraOpen(false)}
          onCapture={handleCameraCapture}
        />
      </CardContent>
    </Card>
  );
}

export default PatientRegistration;
